import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowDownToLine, ArrowUpFromLine, CandlestickChart, Eye, EyeOff, Network, Pencil, Percent, Search, Server, ShieldCheck, X } from 'lucide-react'
import { useState } from 'react'

import { getRegistryMarkets, getRegistryNetworks, patchMarket, patchNetwork, patchNetworkAsset, setSpotFees, type RegistryMarket, type RegistryNetwork } from '../api/admin'
import { PageHeader, PageSection } from '../components/layout'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  Input,
  Modal,
  Panel,
  SegmentedControl,
  Skeleton,
  SkeletonText,
  StatCard,
  Switch,
  Table,
  TableSkeleton,
  Tabs,
  Toggle,
  Tooltip,
  cx,
  describeError,
  money,
  tableClass,
  useToast,
  useTotp,
  type Tone,
} from '../components/ui/Ui'
import styles from './Registry.module.css'

type Tab = 'networks' | 'markets'

/**
 * The registry: which networks and assets accept deposits and withdrawals,
 * at what minimums and fees, and which markets trade at what fee. Every
 * switch asks for the second factor; every change is audited.
 */
export default function Registry(): JSX.Element {
  const [tab, setTab] = useState<Tab>('networks')
  // Both lists are read here too (same keys, one request each) so the tabs can show their counts.
  const networks = useQuery({ queryKey: ['registry-networks'], queryFn: getRegistryNetworks })
  const markets = useQuery({ queryKey: ['registry-markets'], queryFn: getRegistryMarkets })
  return (
    <>
      <PageHeader
        title="Сети и активы"
        description="Где принимаются пополнения и выводы, на каких условиях, и какие рынки торгуются."
        meta={
          <span className={styles.audited}>
            <ShieldCheck aria-hidden="true" />
            Каждое переключение и сохранение — с кодом 2FA и записью в аудит
          </span>
        }
        tabs={
          <Tabs
            variant="underline"
            value={tab}
            onChange={setTab}
            ariaLabel="Реестр"
            options={[
              { value: 'networks', label: 'Сети и активы', icon: <Network />, count: networks.data?.length },
              { value: 'markets', label: 'Рынки и комиссии', icon: <CandlestickChart />, count: markets.data?.length },
            ]}
          />
        }
      />
      <div key={tab} className={styles.tabBody}>
        {tab === 'networks' ? <Networks /> : <Markets />}
      </div>
    </>
  )
}

/* ------------------------------------------------------------------------ */
/* networks                                                                 */
/* ------------------------------------------------------------------------ */

type NetworkFilter = 'all' | 'on' | 'off'
type Asset = RegistryNetwork['assets'][number]

/** Both directions open, one of them, or none. */
function assetTone(asset: Asset): Tone {
  if (asset.depositEnabled && asset.withdrawEnabled) return 'accent'
  if (asset.depositEnabled || asset.withdrawEnabled) return 'warning'
  return 'neutral'
}

const FAMILY_LABEL: Record<string, string> = { evm: 'EVM', tron: 'Tron', solana: 'Solana', bitcoin: 'Bitcoin', utxo: 'UTXO', ton: 'TON', cosmos: 'Cosmos', other: 'другое' }

function Networks(): JSX.Element {
  const [editing, setEditing] = useState<{ network: RegistryNetwork; symbol: string } | null>(null)
  const [form, setForm] = useState({ minDeposit: '', minWithdraw: '', withdrawFee: '' })
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<NetworkFilter>('all')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()
  const list = useQuery({ queryKey: ['registry-networks'], queryFn: getRegistryNetworks })

  const done = (message: string) => (): void => {
    toast(message)
    void queryClient.invalidateQueries({ queryKey: ['registry-networks'] })
  }
  const network = useMutation({
    mutationFn: ({ code, isEnabled, code2 }: { code: string; isEnabled: boolean; watcherAvailable: boolean; code2: string }) =>
      patchNetwork(code, { isEnabled }, code2),
    // The monitor re-reads the registry every five minutes: no restart either way.
    onSuccess: (_data, { isEnabled, watcherAvailable }) =>
      done(
        !isEnabled
          ? 'Сеть выключена: пополнения и выводы в ней больше не предлагаются. Наблюдатель остановится в течение 5 минут.'
          : watcherAvailable
            ? 'Сеть включена. Наблюдатель начнёт её читать в течение 5 минут, перезапуск не нужен.'
            : 'Сеть включена, но наблюдателя для неё в этой сборке нет: пополнения в ней пользователям не показываются.',
      )(),
    onError: (error) => toast(describeError(error), true),
  })
  const asset = useMutation({
    mutationFn: ({ code, symbol, patch, code2 }: { code: string; symbol: string; patch: Parameters<typeof patchNetworkAsset>[2]; code2: string }) => patchNetworkAsset(code, symbol, patch, code2),
    onSuccess: () => {
      done('Актив обновлён')()
      setEditing(null)
    },
    onError: (error) => toast(describeError(error), true),
  })

  const rows = list.data ?? []
  const routes = rows.flatMap((row) => row.assets)
  const enabledCount = rows.filter((row) => row.isEnabled).length
  const blind = rows.filter((row) => row.isEnabled && !row.watcherAvailable).length

  const needle = query.trim().toLowerCase()
  const visible = rows.filter((row) => {
    if (filter === 'on' && !row.isEnabled) return false
    if (filter === 'off' && row.isEnabled) return false
    if (needle === '') return true
    return row.name.toLowerCase().includes(needle) || row.code.toLowerCase().includes(needle) || row.assets.some((item) => item.symbol.toLowerCase().includes(needle))
  })

  // The modal reads the fresh row: a switch inside it changes the list under it.
  const editingNetwork = editing ? (rows.find((row) => row.code === editing.network.code) ?? editing.network) : null
  const current = editing && editingNetwork ? editingNetwork.assets.find((item) => item.symbol === editing.symbol) : undefined

  if (list.isError) {
    return (
      <Card>
        <EmptyState tone="error" icon={<Network />} title="Реестр сетей не загрузился" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
      </Card>
    )
  }

  const pick = (row: RegistryNetwork, item: Asset): void => {
    setEditing({ network: row, symbol: item.symbol })
    setForm({ minDeposit: item.minDeposit, minWithdraw: item.minWithdraw, withdrawFee: item.withdrawFee })
  }
  const toggleNetwork = (row: RegistryNetwork, next: boolean): void =>
    void withTotp((code2) => network.mutateAsync({ code: row.code, isEnabled: next, watcherAvailable: row.watcherAvailable, code2 }))
  const enabledVisible = visible.filter((row) => row.isEnabled)
  const disabledVisible = visible.filter((row) => !row.isEnabled)

  return (
    <div className="page-stack">
      <div className={cx('kpi-grid', 'stagger')}>
        <StatCard label="Сети включены" value={list.isPending ? null : enabledCount} loading={list.isPending} unit={list.isPending ? undefined : `из ${rows.length}`} icon={<Network />} />
        <StatCard
          label="Пополнение открыто"
          value={list.isPending ? null : routes.filter((item) => item.depositEnabled).length}
          loading={list.isPending}
          unit={list.isPending ? undefined : `из ${routes.length}`}
          hint="маршрутов актив · сеть"
          icon={<ArrowDownToLine />}
        />
        <StatCard
          label="Вывод открыт"
          value={list.isPending ? null : routes.filter((item) => item.withdrawEnabled).length}
          loading={list.isPending}
          unit={list.isPending ? undefined : `из ${routes.length}`}
          hint="маршрутов актив · сеть"
          icon={<ArrowUpFromLine />}
        />
        <StatCard
          label="Без наблюдателя"
          value={list.isPending ? null : blind}
          loading={list.isPending}
          icon={blind > 0 ? <EyeOff /> : <Eye />}
          tone={blind > 0 ? 'warning' : 'neutral'}
          hint={blind > 0 ? 'пополнения в них не показываются' : 'все включённые сети читаются'}
        />
      </div>

      <div className={styles.toolbar}>
        <Input
          leading={<Search />}
          placeholder="Сеть или актив, напр. USDT"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQuery('')
          }}
          aria-label="Поиск по сетям и активам"
          className={styles.search}
          trailing={query !== '' ? <IconButton icon={<X />} label="Очистить поиск" size="sm" tooltip={false} onClick={() => setQuery('')} className={styles.clear} /> : undefined}
        />
        <SegmentedControl<NetworkFilter>
          value={filter}
          onChange={setFilter}
          ariaLabel="Какие сети показать"
          options={[
            { value: 'all', label: 'Все', count: rows.length },
            { value: 'on', label: 'Включены', count: enabledCount },
            { value: 'off', label: 'Выключены', count: rows.length - enabledCount },
          ]}
        />
        <div className={styles.legend} aria-label="Обозначения активов">
          <span>
            <i className={cx(styles.legendDot, styles.dotAccent)} />
            пополнение и вывод
          </span>
          <span>
            <i className={cx(styles.legendDot, styles.dotWarning)} />
            одно из двух
          </span>
          <span>
            <i className={styles.legendDot} />
            закрыт
          </span>
        </div>
      </div>

      {list.isPending ? (
        <div className={styles.columns} aria-busy="true">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className={styles.networkCard}>
              <div className={styles.skeletonHead}>
                <Skeleton width={36} height={36} radius={8} />
                <div className={styles.skeletonTitle}>
                  <Skeleton width="50%" height={14} />
                  <Skeleton width="30%" height={10} />
                </div>
              </div>
              <SkeletonText lines={3} />
            </div>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Search />}
            title={rows.length === 0 ? 'Сетей нет' : 'Ничего не нашлось'}
            description={rows.length === 0 ? 'Реестр пуст.' : 'Ни сеть, ни актив не подходят под поиск и фильтр.'}
            action={
              rows.length === 0 ? undefined : (
                <Button
                  onClick={() => {
                    setQuery('')
                    setFilter('all')
                  }}
                >
                  Сбросить
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div>
          {enabledVisible.length > 0 ? (
            <PageSection
              title="Включённые сети"
              description="Пополнения и выводы в них предлагаются пользователям. Нажмите на актив, чтобы изменить минимумы и комиссию."
              actions={<span className={styles.sectionCount}>{enabledVisible.length}</span>}
            >
              <div className={cx(styles.columns, 'stagger')}>
                {enabledVisible.map((row) => (
                  <NetworkCard key={row.code} row={row} needle={needle} onToggle={(next) => toggleNetwork(row, next)} onPick={(item) => pick(row, item)} />
                ))}
              </div>
            </PageSection>
          ) : null}
          {disabledVisible.length > 0 ? (
            <PageSection
              title="Выключенные сети"
              description="Пополнения и выводы в них не предлагаются. После включения наблюдатель подхватит сеть в течение 5 минут."
              actions={<span className={styles.sectionCount}>{disabledVisible.length}</span>}
            >
              <Panel>
                <Table dense ariaLabel="Выключенные сети">
                  <thead>
                    <tr>
                      <th>Сеть</th>
                      <th>Семейство</th>
                      <th className={tableClass.right}>Подтверждений</th>
                      <th>RPC</th>
                      <th>Наблюдатель</th>
                      <th>Активы</th>
                      <th className={tableClass.right}>Включена</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disabledVisible.map((row) => (
                      <tr key={row.code}>
                        <td>
                          <span className={styles.offName}>{row.name}</span>
                          <span className={cx(tableClass.sub, 'num')}>{row.code}</span>
                        </td>
                        <td className={tableClass.muted}>{FAMILY_LABEL[row.family] ?? row.family}</td>
                        <td className={tableClass.num}>{row.confirmationsRequired}</td>
                        <td>{row.hasRpcUrl ? <Badge tone="neutral" size="sm">задан</Badge> : <span className={tableClass.muted}>—</span>}</td>
                        <td>
                          {row.watcherAvailable ? (
                            <Badge tone="neutral" size="sm">
                              есть
                            </Badge>
                          ) : (
                            <Badge tone="warning" size="sm" title="Пополнения в этой сети не показываются пользователям, даже если сеть включена">
                              нет
                            </Badge>
                          )}
                        </td>
                        <td className={tableClass.wrap}>
                          {row.assets.length === 0 ? <span className={tableClass.muted}>нет</span> : <AssetChips row={row} needle={needle} onPick={(item) => pick(row, item)} />}
                        </td>
                        <td className={tableClass.right}>
                          <Switch checked={row.isEnabled} label={`Сеть ${row.name}`} onChange={(next) => toggleNetwork(row, next)} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Panel>
            </PageSection>
          ) : null}
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        icon={<Server />}
        title={editing && editingNetwork ? `${editing.symbol} в сети ${editingNetwork.name}` : ''}
        subtitle="Минимумы и комиссия в единицах актива. Нулевая комиссия — решение заказчика на этом этапе."
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={asset.isPending}
              onClick={() => {
                if (!editing) return
                void withTotp((code2) =>
                  asset.mutateAsync({ code: editing.network.code, symbol: editing.symbol, patch: { minDeposit: form.minDeposit, minWithdraw: form.minWithdraw, withdrawFee: form.withdrawFee }, code2 }),
                )
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        {editing && current ? (
          <div className={styles.modalBody}>
            <div className={styles.toggles}>
              <Toggle
                switchLast
                checked={current.depositEnabled}
                label="Пополнение"
                description="Приём этого актива в этой сети. Применяется сразу, с кодом 2FA."
                onChange={(next) => void withTotp((code2) => asset.mutateAsync({ code: editing.network.code, symbol: editing.symbol, patch: { depositEnabled: next }, code2 }))}
              />
              <Toggle
                switchLast
                checked={current.withdrawEnabled}
                label="Вывод"
                description="Вывод этого актива в эту сеть. Применяется сразу, с кодом 2FA."
                onChange={(next) => void withTotp((code2) => asset.mutateAsync({ code: editing.network.code, symbol: editing.symbol, patch: { withdrawEnabled: next }, code2 }))}
              />
            </div>
            <div className={styles.formGrid}>
              <Field label="Мин. пополнение">
                <Input mono inputMode="decimal" trailing={<span className={styles.unit}>{editing.symbol}</span>} value={form.minDeposit} onChange={(event) => setForm({ ...form, minDeposit: event.target.value })} />
              </Field>
              <Field label="Мин. вывод">
                <Input mono inputMode="decimal" trailing={<span className={styles.unit}>{editing.symbol}</span>} value={form.minWithdraw} onChange={(event) => setForm({ ...form, minWithdraw: event.target.value })} />
              </Field>
              <Field label="Комиссия вывода">
                <Input mono inputMode="decimal" trailing={<span className={styles.unit}>{editing.symbol}</span>} value={form.withdrawFee} onChange={(event) => setForm({ ...form, withdrawFee: event.target.value })} />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  )
}


/** An enabled network: family tile, name and switch; confirmations, RPC and watcher; its assets as chips. */
function NetworkCard({ row, needle, onToggle, onPick }: { row: RegistryNetwork; needle: string; onToggle: (next: boolean) => void; onPick: (item: Asset) => void }): JSX.Element {
  const open = row.assets.filter((item) => item.depositEnabled && item.withdrawEnabled).length
  return (
    <section className={styles.networkCard} aria-label={row.name}>
      <header className={styles.networkHead}>
        <span className={styles.familyTile} aria-hidden="true">
          {(FAMILY_LABEL[row.family] ?? row.family).slice(0, 3).toUpperCase()}
        </span>
        <div className={styles.networkTitle}>
          <h3>{row.name}</h3>
          <div className={styles.networkMeta}>
            <code>{row.code}</code>
            <span aria-hidden="true">·</span>
            <span>{FAMILY_LABEL[row.family] ?? row.family}</span>
          </div>
        </div>
        <div className={styles.networkSwitch}>
          <span className={cx(styles.switchState, row.isEnabled ? styles.switchOn : '')}>{row.isEnabled ? 'Включена' : 'Выключена'}</span>
          <Switch checked={row.isEnabled} label={`Сеть ${row.name}`} onChange={onToggle} />
        </div>
      </header>

      <dl className={styles.facts}>
        <div>
          <dt>Подтверждений</dt>
          <dd className="num">{row.confirmationsRequired}</dd>
        </div>
        <div>
          <dt>RPC</dt>
          <dd>
            {row.hasRpcUrl ? (
              <Badge tone="accent" size="sm">
                задан
              </Badge>
            ) : (
              <Badge tone="warning" size="sm">
                нет
              </Badge>
            )}
          </dd>
        </div>
        <div>
          <dt>Наблюдатель</dt>
          <dd>
            {/* Without a watcher the network is never offered for deposits, even when switched on. */}
            {row.watcherAvailable ? (
              <Badge tone="accent" size="sm" pulse>
                читает
              </Badge>
            ) : (
              <Badge tone="warning" size="sm" title="Пополнения в этой сети не показываются пользователям, даже если сеть включена">
                нет
              </Badge>
            )}
          </dd>
        </div>
      </dl>

      {!row.watcherAvailable ? <p className={styles.warn}>Наблюдателя для этой сети в сборке нет: пополнения в ней пользователям не показываются.</p> : null}

      <div className={styles.assetsHead}>
        <span className="eyebrow">Активы</span>
        <span className={styles.assetsCount}>{row.assets.length}</span>
        {row.assets.length > 0 ? (
          <span className={styles.assetsOpen}>
            открыто полностью <span className="num">{open}</span> из <span className="num">{row.assets.length}</span>
          </span>
        ) : null}
      </div>
      {row.assets.length === 0 ? <p className={styles.noAssets}>Активов в этой сети нет.</p> : <AssetChips row={row} needle={needle} onPick={onPick} />}
    </section>
  )
}

/** Asset chips: two pips (deposit, withdrawal) and the symbol; a click opens the asset editor. */
function AssetChips({ row, needle, onPick }: { row: RegistryNetwork; needle: string; onPick: (item: Asset) => void }): JSX.Element {
  return (
    <div className={styles.assets}>
      {row.assets.map((item) => (
        <Tooltip
          key={item.symbol}
          content={
            <span className={styles.tip}>
              <b>
                {item.symbol} · {row.name}
              </b>
              <span>
                Пополнение {item.depositEnabled ? 'вкл' : 'выкл'} · вывод {item.withdrawEnabled ? 'вкл' : 'выкл'}
              </span>
              <span className="num">
                мин. вывод {item.minWithdraw} · комиссия {item.withdrawFee}
              </span>
            </span>
          }
        >
          <button
            type="button"
            className={cx(styles.asset, styles[`asset_${assetTone(item)}`], needle !== '' && item.symbol.toLowerCase().includes(needle) ? styles.assetMatch : '')}
            aria-label={`${item.symbol}: пополнение ${item.depositEnabled ? 'вкл' : 'выкл'}, вывод ${item.withdrawEnabled ? 'вкл' : 'выкл'}. Изменить`}
            onClick={() => onPick(item)}
          >
            <span className={styles.assetDirs} aria-hidden="true">
              <i data-on={item.depositEnabled || undefined} />
              <i data-on={item.withdrawEnabled || undefined} />
            </span>
            {item.symbol}
          </button>
        </Tooltip>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/* markets                                                                  */
/* ------------------------------------------------------------------------ */

/** A fee as a share of turnover; its percent is in the tooltip: 0.001 → 0.1 %. */
function feeTitle(value: string): string {
  const share = Number(value)
  return Number.isFinite(share) ? `${money(String(share * 100), 2)} % от оборота` : value
}

type MarketFilter = 'all' | 'spot' | 'futures'

function Markets(): JSX.Element {
  const [fees, setFees] = useState({ taker: '', maker: '' })
  const [editing, setEditing] = useState<RegistryMarket | null>(null)
  const [form, setForm] = useState({ takerFee: '', makerFee: '', maxLeverage: '', minNotional: '', maintenanceMarginRate: '' })
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState<MarketFilter>('all')
  const queryClient = useQueryClient()
  const toast = useToast()
  const withTotp = useTotp()
  const list = useQuery({ queryKey: ['registry-markets'], queryFn: getRegistryMarkets })

  const done = (message: string) => (): void => {
    toast(message)
    void queryClient.invalidateQueries({ queryKey: ['registry-markets'] })
  }
  const market = useMutation({
    mutationFn: ({ symbol, patch, code }: { symbol: string; patch: Parameters<typeof patchMarket>[1]; code: string }) => patchMarket(symbol, patch, code),
    onSuccess: done('Рынок обновлён; фид перечитал реестр'),
    onError: (error) => toast(describeError(error), true),
  })
  const all = useMutation({
    mutationFn: ({ code }: { code: string }) => setSpotFees({ ...(fees.taker ? { spotTakerFee: fees.taker } : {}), ...(fees.maker ? { spotMakerFee: fees.maker } : {}) }, code),
    onSuccess: done('Комиссии применены ко всем спотовым рынкам'),
    onError: (error) => toast(describeError(error), true),
  })

  // Spot first: that is what trades today; perpetuals wait for the futures engine.
  const rows = [...(list.data ?? [])].sort((a, b) => (a.marketType === b.marketType ? 0 : a.marketType === 'spot' ? -1 : 1))
  const spotCount = rows.filter((row) => row.marketType === 'spot').length
  const futuresCount = rows.filter((row) => row.marketType === 'futures').length
  const needle = query.trim().toLowerCase()
  const shown = rows.filter((row) => (kind === 'all' || row.marketType === kind) && (needle === '' || row.symbol.toLowerCase().includes(needle)))

  return (
    <div className="page-stack">
      <Card
        icon={<Percent />}
        title="Комиссия для всех спотовых рынков"
        description="Доля от оборота: 0.001 = 0.1 %. Пустое поле не меняется. Сейчас везде 0."
        bodyClassName={styles.bulkBody}
      >
        <div className={styles.bulk}>
          <Field label="Тейкер">
            <Input mono inputMode="decimal" placeholder="напр. 0.001" trailing={<span className={styles.unit}>доля</span>} value={fees.taker} onChange={(event) => setFees({ ...fees, taker: event.target.value })} />
          </Field>
          <Field label="Мейкер">
            <Input mono inputMode="decimal" placeholder="напр. 0.0008" trailing={<span className={styles.unit}>доля</span>} value={fees.maker} onChange={(event) => setFees({ ...fees, maker: event.target.value })} />
          </Field>
          <Button variant="primary" disabled={!fees.taker && !fees.maker} loading={all.isPending} onClick={() => void withTotp((code) => all.mutateAsync({ code }))} className={styles.bulkButton}>
            Применить ко всем спотовым{list.data ? ` (${spotCount})` : ''}
          </Button>
        </div>
      </Card>

      <div className={styles.toolbar}>
        <Input
          leading={<Search />}
          placeholder="Пара, напр. BTC"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Escape') setQuery('')
          }}
          aria-label="Поиск по рынкам"
          className={styles.search}
          trailing={query !== '' ? <IconButton icon={<X />} label="Очистить поиск" size="sm" tooltip={false} onClick={() => setQuery('')} className={styles.clear} /> : undefined}
        />
        <SegmentedControl<MarketFilter>
          value={kind}
          onChange={setKind}
          ariaLabel="Тип рынка"
          options={[
            { value: 'all', label: 'Все', count: rows.length },
            { value: 'spot', label: 'Спот', count: spotCount },
            { value: 'futures', label: 'Бессрочные', count: futuresCount },
          ]}
        />
      </div>

      <Panel>
        {list.isError ? (
          <EmptyState tone="error" icon={<CandlestickChart />} title="Рынки не загрузились" description={describeError(list.error)} action={<Button onClick={() => void list.refetch()}>Повторить</Button>} />
        ) : list.isPending ? (
          <TableSkeleton rows={6} headers={['Пара', 'Тейкер', 'Мейкер', 'Плечо до', 'Поддерж. маржа', 'Мин. объём', 'Торгуется', '']} />
        ) : shown.length === 0 ? (
          <EmptyState
            icon={<CandlestickChart />}
            title={rows.length === 0 ? 'Рынков нет' : 'Ничего не нашлось'}
            description={rows.length === 0 ? 'Реестр рынков пуст.' : 'Ни одна пара не подходит под поиск и фильтр.'}
            action={
              rows.length === 0 ? undefined : (
                <Button
                  onClick={() => {
                    setQuery('')
                    setKind('all')
                  }}
                >
                  Сбросить
                </Button>
              )
            }
          />
        ) : (
          <Table ariaLabel="Рынки">
            <thead>
              <tr>
                <th>Пара</th>
                <th className={tableClass.right} title="Доля от оборота: 0.001 = 0.1 %">
                  Тейкер
                </th>
                <th className={tableClass.right} title="Доля от оборота: 0.001 = 0.1 %">
                  Мейкер
                </th>
                <th className={tableClass.right}>Плечо до</th>
                <th className={tableClass.right}>Поддерж. маржа</th>
                <th className={tableClass.right}>Мин. объём, USDT</th>
                <th>Торгуется</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => (
                <tr key={row.symbol}>
                  <td>
                    <div className={styles.pair}>
                      <span className={cx('num', styles.pairSymbol)}>{row.symbol}</span>
                      <Badge size="sm" dot={false} tone={row.marketType === 'futures' ? 'info' : 'neutral'}>
                        {row.marketType === 'futures' ? 'бессрочный' : row.marketType === 'spot' ? 'спот' : row.marketType}
                      </Badge>
                    </div>
                  </td>
                  <td className={tableClass.num} title={feeTitle(row.takerFee)}>
                    {money(row.takerFee, 4)}
                  </td>
                  <td className={tableClass.num} title={feeTitle(row.makerFee)}>
                    {money(row.makerFee, 4)}
                  </td>
                  <td className={tableClass.num}>{row.maxLeverage}×</td>
                  <td className={tableClass.num}>{row.marketType === 'futures' ? `${money(String(Number(row.maintenanceMarginRate) * 100), 2)} %` : '—'}</td>
                  <td className={tableClass.num}>{money(row.minNotional, 0)}</td>
                  <td>
                    <div className={styles.tradeCell}>
                      <Switch checked={row.isEnabled} label={`Рынок ${row.symbol}`} onChange={(next) => void withTotp((code) => market.mutateAsync({ symbol: row.symbol, patch: { isEnabled: next }, code }))} />
                      <span className={cx(styles.switchState, row.isEnabled ? styles.switchOn : '')}>{row.isEnabled ? 'да' : 'нет'}</span>
                    </div>
                  </td>
                  <td className={cx(tableClass.actions, tableClass.reveal)}>
                    <Button
                      size="sm"
                      variant="ghost"
                      icon={<Pencil />}
                      onClick={() => {
                        setEditing(row)
                        setForm({ takerFee: row.takerFee, makerFee: row.makerFee, maxLeverage: String(row.maxLeverage), minNotional: row.minNotional, maintenanceMarginRate: row.maintenanceMarginRate })
                      }}
                    >
                      Изменить
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        icon={<CandlestickChart />}
        title={editing?.symbol ?? ''}
        subtitle={editing?.marketType === 'futures' ? 'Бессрочный контракт: плечо и поддерживающая маржа задают цену ликвидации.' : 'Спотовая пара.'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={market.isPending}
              onClick={() => {
                if (!editing) return
                void withTotp((code) =>
                  market
                    .mutateAsync({
                      symbol: editing.symbol,
                      patch: {
                        takerFee: form.takerFee,
                        makerFee: form.makerFee,
                        minNotional: form.minNotional,
                        ...(editing.marketType === 'futures' ? { maxLeverage: Number(form.maxLeverage), maintenanceMarginRate: form.maintenanceMarginRate } : {}),
                      },
                      code,
                    })
                    .then((result) => {
                      setEditing(null)
                      return result
                    }),
                )
              }}
            >
              Сохранить
            </Button>
          </>
        }
      >
        {editing ? (
          <div className={styles.formGrid}>
            <Field label="Тейкер" hint="Доля от оборота: 0.001 = 0.1 %.">
              <Input mono inputMode="decimal" trailing={<span className={styles.unit}>доля</span>} value={form.takerFee} onChange={(event) => setForm({ ...form, takerFee: event.target.value })} />
            </Field>
            <Field label="Мейкер">
              <Input mono inputMode="decimal" trailing={<span className={styles.unit}>доля</span>} value={form.makerFee} onChange={(event) => setForm({ ...form, makerFee: event.target.value })} />
            </Field>
            <Field label="Мин. объём">
              <Input mono inputMode="decimal" trailing={<span className={styles.unit}>USDT</span>} value={form.minNotional} onChange={(event) => setForm({ ...form, minNotional: event.target.value })} />
            </Field>
            {editing.marketType === 'futures' ? (
              <>
                <Field label="Плечо до" hint="1–100.">
                  <Input mono type="number" min={1} max={100} trailing={<span className={styles.unit}>×</span>} value={form.maxLeverage} onChange={(event) => setForm({ ...form, maxLeverage: event.target.value })} />
                </Field>
                <Field label="Поддерживающая маржа" hint="Доля позиции, ниже которой движок ликвидирует: 0.005 = 0.5 %." className={styles.wide}>
                  <Input mono inputMode="decimal" trailing={<span className={styles.unit}>доля</span>} value={form.maintenanceMarginRate} onChange={(event) => setForm({ ...form, maintenanceMarginRate: event.target.value })} />
                </Field>
              </>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}
